import { Badge } from '@monksflow/monks-ui';
import clsx from 'clsx';

import type { TitledItemDto } from '@inference.engine/api-client';
import { AnalysisMarkdown } from '../../../components';
import { toTitledItem } from '../../../utils/titledItem';

interface BorderedChipGridProps {
  items: (string | TitledItemDto)[];
  /** Columns at `lg` and up; the grid stays single-column below that. */
  columns?: 2 | 3 | 4;
  /**
   * Builds each card's badge from its 1-based position, e.g. `(n) => t('...', { number: n })`.
   * Omit it on a list whose entries read fine unnumbered, and the cards lose the badge and
   * left-align instead.
   */
  badgeLabel?: (position: number) => string;
}

const COLUMN_CLASS: Record<2 | 3 | 4, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

/** Grid of bordered cards (key partners, marketing channels, customer acquisition). */
export const BorderedChipGrid = ({ items, columns = 3, badgeLabel }: BorderedChipGridProps) => (
  <div className={clsx('grid grid-cols-1 gap-3', COLUMN_CLASS[columns])}>
    {items.map(toTitledItem).map(({ title, subtitle }, i) => (
      <div
        key={`${title}-${i}`}
        className={clsx(
          'flex flex-col gap-1 rounded-xl border border-border-light p-xl',
          badgeLabel ? 'items-center text-center' : 'items-start',
        )}
      >
        {badgeLabel && (
          <Badge
            variant="secondary"
            size="sm"
            roundness="full"
            className="mb-2 dark:bg-surface-raised"
          >
            {badgeLabel(i + 1)}
          </Badge>
        )}
        {/* A badged card leads with the name as a heading; an unbadged one keeps it
            at body size, where it reads as the first line of the entry. */}
        <AnalysisMarkdown
          text={title}
          className={clsx(
            'font-medium text-foreground',
            badgeLabel ? 'text-heading-sm' : 'text-body-md',
          )}
        />
        {subtitle && (
          <AnalysisMarkdown text={subtitle} className="text-body-md text-foreground-alt" />
        )}
      </div>
    ))}
  </div>
);
